import { createContext, useContext, useMemo } from "react"

import type {
  OperatorResolvedSession,
} from "@/features/operator/shared/operator-access"
import {
  resolveOperatorShellFrameState,
} from "@/features/operator/shared/operator-shell-frame-state"

export type OperatorShellSessionContextValue =
  | {
      status: "loading"
    }
  | {
      message: string
      status: "error"
    }
  | {
      session: OperatorResolvedSession
      status: "ready"
    }

export const OperatorShellSessionContext =
  createContext<OperatorShellSessionContextValue | null>(null)

export function useOperatorShellSession() {
  const context = useContext(OperatorShellSessionContext)

  if (!context) {
    throw new Error(
      "useOperatorShellSession must be used within OperatorShellSessionProvider."
    )
  }

  return context
}

export function useOptionalOperatorShellSession() {
  return useContext(OperatorShellSessionContext)
}

export function useOperatorShellFrameState(pathname: string) {
  const state = useOperatorShellSession()

  return useMemo(() => {
    if (state.status !== "ready") {
      return state
    }

    return {
      ...resolveOperatorShellFrameState(pathname, state.session),
      session: state.session,
      status: state.status,
    }
  }, [pathname, state])
}
